import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
  Chip,
  Card,
  CardContent,
  Divider,
  Button
} from '@mui/material';
import {
  Speed,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
  Lightbulb,
  Refresh
} from '@mui/icons-material';
import AILoadingAnimation from './AILoadingAnimation';

const ProjectAIAnalysis = ({ analysis, loading, onRefresh }) => {
  if (loading) {
    return <AILoadingAnimation />;
  }

  if (!analysis) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" gutterBottom>
          Aucune analyse disponible
        </Typography>
        <Button variant="contained" startIcon={<Refresh />} onClick={onRefresh}>
          Lancer l'analyse
        </Button>
      </Box>
    );
  }

  const { health_score, status, suggestions, predictions } = analysis;

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 50) return 'warning';
    return 'error';
  };

  const getStatusColor = (statusStr) => {
    const s = statusStr?.toLowerCase();
    if (s?.includes('excellent') || s?.includes('good')) return 'success';
    if (s?.includes('warning') || s?.includes('risk')) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* En-tête avec Score et Statut */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
              <CircularProgress 
                variant="determinate" 
                value={health_score} 
                size={120} 
                thickness={5}
                color={getScoreColor(health_score)}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h3" component="div" color="text.secondary" fontWeight="bold">
                  {health_score}
                </Typography>
              </Box>
            </Box>
            <Typography variant="h6" gutterBottom>
              Score de Santé
            </Typography>
            <Chip 
              label={status?.toUpperCase() || 'INCONNU'} 
              color={getStatusColor(status)} 
              variant="outlined" 
              icon={<Speed />}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" display="flex" alignItems="center">
                <TrendingUp sx={{ mr: 1 }} /> Prédictions & Risques
              </Typography>
              <Button startIcon={<Refresh />} onClick={onRefresh} size="small">
                Actualiser
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Probabilité de finir à temps
                    </Typography>
                    <Typography variant="h4" color={predictions?.on_time_probability > 70 ? 'success.main' : 'warning.main'}>
                      {predictions?.on_time_probability}%
                    </Typography>
                    <Schedule sx={{ float: 'right', color: 'text.secondary', opacity: 0.5, mt: -4 }} fontSize="large" />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Date de fin estimée
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {predictions?.estimated_completion || 'Non estimée'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Niveau de risque global
                    </Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        color: predictions?.risk_level === 'high' ? 'error.main' : 
                               predictions?.risk_level === 'medium' ? 'warning.main' : 'success.main',
                        textTransform: 'uppercase'
                      }}
                    >
                      {predictions?.risk_level || 'INCONNU'}
                    </Typography>
                    <Warning sx={{ float: 'right', color: 'text.secondary', opacity: 0.5, mt: -4 }} fontSize="large" />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Suggestions d'amélioration */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom display="flex" alignItems="center">
          <Lightbulb sx={{ mr: 1, color: 'warning.main' }} /> Suggestions d'amélioration
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {suggestions && suggestions.length > 0 ? (
          <List>
            {suggestions.map((suggestion, index) => (
              <ListItem key={index} alignItems="flex-start" sx={{ bgcolor: 'background.default', mb: 1, borderRadius: 1 }}>
                <ListItemIcon>
                  {suggestion.type === 'warning' ? <Warning color="warning" /> : 
                   suggestion.type === 'success' ? <CheckCircle color="success" /> : 
                   <Lightbulb color="info" />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="bold">
                      {suggestion.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.primary" display="block">
                        {suggestion.message}
                      </Typography>
                      {suggestion.action && (
                        <Typography component="span" variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                          👉 Action : {suggestion.action}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Alert severity="info">Aucune suggestion particulière pour le moment. Continuez comme ça !</Alert>
        )}
      </Paper>
    </Box>
  );
};

export default ProjectAIAnalysis;
